import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSuiClient } from '@mysten/dapp-kit';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Circle, Coins, Loader2, Sparkles, Trophy } from 'lucide-react';

import { Dialog, DialogContent, DialogFooter } from './ui/dialog';
import { Button } from './ui';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { SaintPatrickStreakLeaderCard } from './saint-patrick-streak-leader-card';
import { useAuth } from '@/hooks';
import { useLoginModalStore } from '@/stores/login-modal-store';
import { SAINT_PATRICK_EVENT_CFG } from '@/configs/saint-patrick-event-config';
import { campaignRepository } from '@/repositories/campaign-repository';
import { questRepository } from '@/repositories/quest-repository';
import { nftRepository } from '@/repositories/nft-repository';
import { QueryKeys } from '@/consts';
import { cn } from '@/utils';
import { formatDateRange, INTEGER_FORMATTER, NUMBER_FORMATTER } from '@/utils/format-date-range';
import type { NftSpec, SuigarNft } from '@/dtos';

interface SaintPatrickWelcomeModalProps {
	isOpen: boolean;
	onClose: () => void;
}

type BonusNftDetails = {
	key: string;
	owned: boolean;
	name: string;
	imageUrl: string | null;
	isLoading: boolean;
	specId?: string;
};

const timeframeLabel = formatDateRange(SAINT_PATRICK_EVENT_CFG.timeframe.start, SAINT_PATRICK_EVENT_CFG.timeframe.displayEnd);
const DEFAULT_NFT_NAME = 'Event NFT';

const SaintPatrickWelcomeModal = ({ isOpen, onClose }: SaintPatrickWelcomeModalProps) => {
	const { isLogged, currentAccount, address: primaryAddress } = useAuth();
	const address = primaryAddress ?? currentAccount?.address ?? null;
	const { openLoginModal } = useLoginModalStore();
	const navigate = useNavigate();
	const client = useSuiClient();
	const { qualification, ticketing, prizes, artwork, name } = SAINT_PATRICK_EVENT_CFG;

	const qualificationQuery = useQuery({
		queryKey: ['saint-patrick', 'qualification', address],
		queryFn: async () => {
			const response = await questRepository.checkQuestStatus(SAINT_PATRICK_EVENT_CFG.slug, qualification.questKey, address!);
			return response.result;
		},
		enabled: Boolean(isOpen && isLogged && address),
		staleTime: 20_000,
		refetchInterval: isOpen ? 20_000 : false,
	});

	const ticketSummaryQuery = useQuery({
		queryKey: ['saint-patrick', 'wager', address],
		queryFn: () =>
			campaignRepository.getTicketSummary({
				address: address!,
				start: SAINT_PATRICK_EVENT_CFG.timeframe.start,
				end: SAINT_PATRICK_EVENT_CFG.timeframe.end,
				path: ticketing.apiPath,
			}),
		enabled: Boolean(isOpen && isLogged && address),
		staleTime: 20_000,
		refetchInterval: isOpen ? 20_000 : false,
	});

	const nftFactoryQuery = useQuery({
		queryKey: [QueryKeys.NftFactory],
		queryFn: () => nftRepository.getNftFactory(client),
		enabled: Boolean(isOpen),
		staleTime: 10 * 60_000,
	});

	const ownedNftsQuery = useQuery({
		queryKey: [QueryKeys.OwnedNft, address],
		queryFn: () => nftRepository.getOwnedNft(client, address!),
		enabled: Boolean(isOpen && isLogged && address),
		staleTime: 2 * 60_000,
	});

	const normalizedOwnedNames = useMemo(() => {
		if (!ownedNftsQuery.data) return [];
		return ownedNftsQuery.data.map((nft: SuigarNft) => nft.name.toLowerCase());
	}, [ownedNftsQuery.data]);

	const bonusDetails: BonusNftDetails[] = useMemo(() => {
		const specs = nftFactoryQuery.data?.specs ?? [];
		return ticketing.bonusNfts.map((bonus, index) => {
			const matcher = bonus.nameIncludes.toLowerCase().trim();
			const matchedSpec = specs.find((spec: NftSpec) => spec.name.toLowerCase().includes(matcher));
			const owned = normalizedOwnedNames.some((item: string) => item.includes(matcher));

			return {
				key: matcher || `bonus-${index}`,
				owned,
				name: matchedSpec?.name ?? DEFAULT_NFT_NAME,
				imageUrl: matchedSpec?.url ?? null,
				specId: matchedSpec?.id,
				isLoading: nftFactoryQuery.isLoading || (isLogged && ownedNftsQuery.isLoading),
			};
		});
	}, [isLogged, nftFactoryQuery.data, nftFactoryQuery.isLoading, normalizedOwnedNames, ownedNftsQuery.isLoading, ticketing.bonusNfts]);

	const qualificationGoal = qualificationQuery.data?.goal ?? qualification.requiredWins;
	const qualificationProgress = Math.min(qualificationQuery.data?.progress ?? 0, qualificationGoal);
	const isQualified = Boolean(qualificationQuery.data?.isValid);
	const totalWagered = ticketSummaryQuery.data?.totalWagered ?? 0;
	const bonusTickets = Math.floor(totalWagered / ticketing.additionalTicketStep);
	const baseTickets = isQualified ? 1 + bonusTickets : 0;
	const ownedBonusStatuses = ticketing.bonusNfts.map((_, index) => bonusDetails[index]?.owned ?? false);
	const ownedBonusEntries = ticketing.bonusNfts.reduce<{ index: number; multiplier: number }[]>((acc, bonus, index) => {
		if (ownedBonusStatuses[index]) {
			acc.push({ index, multiplier: bonus.multiplier });
		}
		return acc;
	}, []);
	const ownedMultipliers = ownedBonusEntries.map((entry) => entry.multiplier);
	const combinedBonusMultiplier =
		ownedMultipliers.length === 0
			? 1
			: ticketing.bonusCombinationStrategy === 'multiply'
				? ownedMultipliers.reduce((acc, value) => acc * value, 1)
				: Math.max(...ownedMultipliers);
	const highestOwnedMultiplier = ticketing.bonusCombinationStrategy === 'max' && ownedBonusEntries.length > 0 ? Math.max(...ownedMultipliers) : null;
	const finalMultiplier = ticketing.baseMultiplier * combinedBonusMultiplier;
	const finalTickets = Math.round(baseTickets * finalMultiplier);
	const ownedBonusCount = ownedBonusStatuses.filter(Boolean).length;
	const hasActiveBonus = ownedBonusCount > 0;
	const isStep2Complete = isQualified && bonusTickets > 0;
	const isStep3Complete = ownedBonusCount > 0;
	const ticketCountdown = ticketing.additionalTicketStep - (totalWagered % ticketing.additionalTicketStep || 0);
	const normalizedCountdown = ticketCountdown === 0 ? ticketing.additionalTicketStep : ticketCountdown;
	const nextTicketProgress = ((totalWagered % ticketing.additionalTicketStep) / ticketing.additionalTicketStep) * 100;
	const multiplierText = ownedMultipliers.length > 0 ? `1 × ${ownedMultipliers.map((value) => NUMBER_FORMATTER.format(value)).join(' × ')}` : '1 × 1';
	const handleClose = () => onClose();

	const handlePrimaryAction = () => {
		if (isLogged) {
			navigate({ to: qualification.playLink });
		} else {
			openLoginModal();
		}
		onClose();
	};

	const handleNftClick = (target?: string) => {
		navigate({
			to: '/nft',
			search: (prev?: Record<string, unknown>) => ({
				...(prev ?? {}),
				focus: target ?? undefined,
			}),
		});
		onClose();
	};
	const isBonusActive = (index: number) => {
		if (!ownedBonusStatuses[index]) return false;
		if (ticketing.bonusCombinationStrategy === 'max') {
			if (highestOwnedMultiplier === null) return false;
			return ticketing.bonusNfts[index].multiplier === highestOwnedMultiplier;
		}
		return true;
	};

	const drawWinnerCount = prizes.draw.length;
	const drawPrizePool = prizes.draw.reduce((sum, prize) => sum + prize, 0);
	const totalWinnerCount = drawWinnerCount + 1;
	const drawPrizeSummary = Array.from(
		prizes.draw.reduce((acc, prize) => {
			acc.set(prize, (acc.get(prize) ?? 0) + 1);
			return acc;
		}, new Map<number, number>()),
	)
		.sort((left, right) => right[0] - left[0])
		.map(([prize, count]) => `${count}×$${NUMBER_FORMATTER.format(prize)}`)
		.join(', ');
	const totalPrizePool = drawPrizePool + prizes.streakLeader;
	const ticketFormulaText = isQualified
		? `${INTEGER_FORMATTER.format(baseTickets)} base × ${multiplierText} = ${INTEGER_FORMATTER.format(finalTickets)}`
		: `${qualification.requiredWins} straight CoinFlip wins unlock your first entry.`;
	const nftBoostSummary = ticketing.bonusNfts.map((bonus) => `×${NUMBER_FORMATTER.format(bonus.multiplier)} ${bonus.nameIncludes}`).join(' · ');
	const nftSectionSummary =
		ticketing.bonusCombinationStrategy === 'multiply' ? 'Both boosts stack when owned.' : 'Only the highest owned boost applies.';

	return (
		<Dialog open={isOpen} onOpenChange={(open: boolean) => !open && handleClose()}>
			<DialogContent className="max-w-4xl border border-border/50 bg-background/95 p-0 shadow-2xl">
				<img
					src={artwork.logoFull}
					alt={name}
					className="pointer-events-none absolute left-1/2 top-0 z-10 w-64 max-w-[72vw] -translate-x-1/2 -translate-y-[47%] transform object-contain transition-all duration-500 sm:w-72"
				/>
				<div className="relative">
					<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(115,179,53,0.18),transparent_55%),linear-gradient(135deg,rgba(255,255,255,0.04),transparent_45%)]" />
					<img
						src={artwork.buttonIcon}
						alt=""
						className="pointer-events-none absolute bottom-12 left-3 hidden w-20 -rotate-[18deg] opacity-15 sm:block lg:bottom-10 lg:left-6 lg:w-24"
					/>
					<img
						src={artwork.buttonAccent}
						alt=""
						className="pointer-events-none absolute right-4 top-[44%] hidden w-10 -translate-y-1/2 rotate-12 opacity-30 md:block lg:right-6 lg:w-12"
					/>
					<div className="relative flex max-h-[85dvh] w-full flex-col overflow-y-auto">
						<div className="space-y-5 p-4 pt-[3.75rem] sm:px-6 sm:pb-6 sm:pt-[4.5rem]">
							<div className="flex flex-col items-center gap-4 text-center">
								<div className="flex flex-wrap items-center justify-center gap-2">
									<Badge variant="success" className="uppercase tracking-[0.18em]">
										Saint Patrick Event
									</Badge>
									<Badge variant="outline" className="uppercase tracking-wide text-muted-foreground">
										{timeframeLabel}
									</Badge>
									<Badge variant="outline" className="uppercase tracking-wide text-muted-foreground">
										{totalWinnerCount} winners
									</Badge>
								</div>
								<div className="space-y-2">
									<h2 className="leading-none tracking-tight">
										<span className="block origin-center pb-1 -rotate-2 scale-[1.04] font-shadow-stroke-3xl font-serif text-5xl sm:scale-[1.07] sm:text-6xl">
											$150 to win!
										</span>
									</h2>
									<p className="mx-auto max-w-2xl text-sm text-muted-foreground sm:text-base">
											Hit 3 CoinFlip wins in a row, then stack tickets every 10 SUI wagered on any game from {timeframeLabel}.<br />
										Let your NFTs multiply the total!
									</p>
								</div>
								{/* <div className="grid w-full gap-2 text-left sm:grid-cols-3">
										<div className="rounded-2xl border border-success/25 bg-success/10 px-3 py-2.5">
											<p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-success">Qualify</p>
											<p className="mt-1 text-lg font-semibold">3 wins in a row</p>
											<p className="text-xs text-muted-foreground">Min 1 SUI each</p>
										</div>
										<div className="rounded-2xl border border-primary/25 bg-primary/8 px-3 py-2.5">
											<p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">Extra Tickets</p>
											<p className="mt-1 text-lg font-semibold">+1 / 10 SUI</p>
												<p className="text-xs text-muted-foreground">Any game from {timeframeLabel}</p>
										</div>
										<div className="rounded-2xl border border-warning/25 bg-warning/10 px-3 py-2.5">
											<p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-warning">NFT Boosts</p>
											<p className="mt-1 text-lg font-semibold">Stacked</p>
											<p className="text-xs text-muted-foreground">{nftBoostSummary}</p>
										</div>
									</div> */}
							</div>

							<div className="space-y-4">
								<div className="rounded-3xl border border-success/25 bg-success/10 p-4 shadow-sm">
									<div className="space-y-2">
										<div className="flex flex-wrap items-start justify-between gap-2">
											<div className="flex min-w-0 items-center gap-3">
												<div className="shrink-0 rounded-2xl bg-success/15 p-2 text-success">
													<CheckCircle2 className="h-5 w-5" />
												</div>
												<div className="min-w-0 space-y-0.5">
													<p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-success">Step 1</p>
													<h3 className="text-base font-semibold sm:text-lg">Win 3 CoinFlip in a row</h3>
												</div>
											</div>
											<Badge variant={isQualified ? 'success' : 'secondary'}>
												{isQualified ? 'Qualified' : `${qualificationProgress}/${qualificationGoal}`}
											</Badge>
										</div>
										<p className="text-sm text-muted-foreground">
											Each round must be at least {qualification.minStakeSui} {ticketing.wageringCoinSymbol}.
										</p>
										<div className="space-y-2">
											<div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
												<span>Progress</span>
												<span>{qualificationQuery.isLoading ? 'Loading…' : `${qualificationProgress} / ${qualificationGoal}`}</span>
											</div>
											<Progress
												value={(qualificationProgress / qualificationGoal) * 100}
												indicatorClassName={isQualified ? 'bg-success' : 'bg-primary'}
											/>
										</div>
									</div>
								</div>

								<div
									className={cn(
										'rounded-3xl border p-4 shadow-sm',
										isStep2Complete
											? 'border-success/25 bg-success/10'
											: isQualified
												? 'border-primary/35 bg-primary/8'
												: 'border-border/60 bg-card/80',
									)}
								>
									<div className="space-y-3">
										<div className="flex flex-wrap items-start justify-between gap-2">
											<div className="flex min-w-0 items-center gap-3">
												<div
													className={cn('shrink-0 rounded-2xl p-2', isStep2Complete ? 'bg-success/15 text-success' : 'bg-primary/12 text-primary')}
												>
													<Coins className="h-5 w-5" />
												</div>
												<div className="min-w-0 space-y-0.5">
													<p
														className={cn('text-[11px] font-semibold uppercase tracking-[0.18em]', isStep2Complete ? 'text-success' : 'text-primary')}
													>
														Step 2
													</p>
													<h3 className="text-base font-semibold sm:text-lg">Every 10 SUI on any game = 1 extra ticket!</h3>
												</div>
											</div>
											<Badge variant={isStep2Complete ? 'success' : isQualified ? 'primary' : 'outline'}>
												{isQualified ? `+${INTEGER_FORMATTER.format(bonusTickets)} tickets` : `+${INTEGER_FORMATTER.format(bonusTickets)} banked`}
											</Badge>
										</div>
										<p className="text-sm text-muted-foreground">
											{isQualified
													? `Your total wager across all games from ${timeframeLabel} is ${NUMBER_FORMATTER.format(totalWagered)} ${ticketing.wageringCoinSymbol}.`
													: `Your wager across all games from ${timeframeLabel} is already tracked. Step 1 unlocks the tickets.`}
										</p>
										<div className="rounded-2xl border border-border/50 bg-background/70 p-3">
											<div className="flex flex-wrap items-end justify-between gap-4">
												<div>
													<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Bonus tickets</p>
													<p className="mt-1 text-3xl font-semibold leading-none">+{INTEGER_FORMATTER.format(bonusTickets)}</p>
												</div>
												<div className="text-right">
													<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Next +1</p>
													<p className="mt-1 text-lg font-semibold">
														{isQualified ? `${NUMBER_FORMATTER.format(normalizedCountdown)} ${ticketing.wageringCoinSymbol}` : 'Qualify first'}
													</p>
												</div>
											</div>
											<p className="mt-3 text-xs text-muted-foreground">
													Every {ticketing.additionalTicketStep} {ticketing.wageringCoinSymbol} wagered on any game from {timeframeLabel} = 1 more
													ticket.
											</p>
											{isQualified ? (
												<div className="mt-3 space-y-2">
													<div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
														<span>Next ticket</span>
														<span>
															{ticketSummaryQuery.isLoading
																? 'Loading…'
																: `${NUMBER_FORMATTER.format(totalWagered % ticketing.additionalTicketStep)} / ${ticketing.additionalTicketStep}`}
														</span>
													</div>
													<Progress value={nextTicketProgress} indicatorClassName="bg-primary" />
												</div>
											) : null}
										</div>
									</div>
								</div>

								<div
									className={cn(
										'rounded-3xl border p-4 shadow-sm',
										isStep3Complete ? 'border-success/25 bg-success/10' : 'border-warning/25 bg-warning/10',
									)}
								>
									<div className="space-y-3">
										<div className="flex flex-wrap items-start justify-between gap-2">
											<div className="flex min-w-0 items-center gap-3">
												<div
													className={cn('shrink-0 rounded-2xl p-2', isStep3Complete ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning')}
												>
													<Sparkles className="h-5 w-5" />
												</div>
												<div className="min-w-0 space-y-0.5">
													<p
														className={cn('text-[11px] font-semibold uppercase tracking-[0.18em]', isStep3Complete ? 'text-success' : 'text-warning')}
													>
														Step 3
													</p>
													<h3 className="text-base font-semibold sm:text-lg">Add NFT boosts</h3>
												</div>
											</div>
											<Badge variant={isStep3Complete ? 'success' : 'outline'}>
												{hasActiveBonus ? `×${NUMBER_FORMATTER.format(finalMultiplier)}` : 'Optional'}
											</Badge>
										</div>
										<p className="text-sm text-muted-foreground">Own Cane for ×2 and Slush for ×1.5. {nftSectionSummary}</p>
										<div className="grid gap-3 sm:grid-cols-2">
											{ticketing.bonusNfts.map((bonus, index) => {
												const detail = bonusDetails[index];
												const isOwned = ownedBonusStatuses[index];
												const isActive = isBonusActive(index);
												const imageUrl = detail?.imageUrl ?? null;
												const hasImage = Boolean(imageUrl);
												const displayName = detail?.name ?? DEFAULT_NFT_NAME;
												const focusTarget = detail?.specId ?? detail?.key ?? displayName;
												return (
													<div
														key={detail?.key ?? bonus.nameIncludes}
														role="button"
														tabIndex={0}
														className={cn(
															'flex flex-col gap-2 rounded-2xl border p-2.5 text-left shadow-sm transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning/40 sm:p-3',
															isActive
																? 'border-success/60 bg-success/10'
																: isOwned
																	? 'border-warning/40 bg-warning/10'
																	: 'border-border/60 bg-card/60',
														)}
														onClick={() => handleNftClick(focusTarget)}
														onKeyDown={(event) => {
															if (event.key === 'Enter' || event.key === ' ') {
																event.preventDefault();
																handleNftClick(focusTarget);
															}
														}}
													>
														<div className="flex w-full items-start justify-between gap-3">
															<div className="min-w-0">
																<p className="truncate text-sm font-semibold">{displayName}</p>
																<p className="text-[11px] text-muted-foreground">×{NUMBER_FORMATTER.format(bonus.multiplier)} multiplier</p>
															</div>
															<Badge variant={isOwned ? 'secondary' : 'outline'} size="sm" className="uppercase tracking-wide">
																{isOwned ? (isActive ? 'Active' : 'Owned') : 'Mint me'}
															</Badge>
														</div>
														<div className="relative pt-1">
															<div className="relative flex h-32 w-full items-center justify-center rounded-xl border border-border/40 bg-background/75 p-3 sm:h-36 lg:h-40">
																{detail?.isLoading ? (
																	<div className="absolute inset-0 flex items-center justify-center rounded-xl bg-background/60">
																		<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
																	</div>
																) : null}
																{hasImage ? (
																	<div className="flex h-full w-full items-center justify-center rounded-lg bg-background/30">
																		<img
																			src={imageUrl ?? undefined}
																			alt={displayName}
																			className="max-h-full max-w-full object-contain"
																			loading="lazy"
																		/>
																	</div>
																) : (
																	<span className="px-4 text-center text-xs font-medium text-muted-foreground">Preview unavailable</span>
																)}
															</div>
															<Badge
																variant={isActive ? 'success' : 'warning'}
																className="absolute right-2 top-0 rounded-full px-2 py-0 font-semibold uppercase tracking-wide shadow-sm"
															>
																<span className="font-shadow-stroke-2xl font-serif -mt-0.5 text-[14px]">×{bonus.multiplier}</span>
															</Badge>
														</div>
														<div className="flex w-full items-center justify-between gap-2 text-xs text-muted-foreground">
															<div className="flex min-w-0 items-center gap-1.5">
																{detail?.isLoading ? (
																	<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
																) : isOwned ? (
																	<CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />
																) : (
																	<Circle className="h-3.5 w-3.5 shrink-0" />
																)}
																<span className="truncate">
																	{detail?.isLoading
																		? 'Checking wallet…'
																		: isOwned
																			? isActive
																				? 'Owned and active'
																				: 'Owned in wallet'
																			: 'Not owned'}
																</span>
															</div>
															{!isOwned ? (
																<Button
																	variant="primary"
																	size="sm"
																	className="h-7 px-2.5 text-[11px] font-semibold"
																	onClick={(event) => {
																		event.stopPropagation();
																		handleNftClick(focusTarget);
																	}}
																>
																	Mint now
																</Button>
															) : null}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								</div>

								<details className="group rounded-3xl border border-border/60 bg-card/80 shadow-sm">
									<summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 [&::-webkit-details-marker]:hidden">
										<div>
											<p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Details</p>
											<h3 className="text-lg font-semibold">Tickets and prize breakdown</h3>
											<p className="text-sm text-muted-foreground">Open for your current stack and the full payout split.</p>
										</div>
										<div className="flex shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-center">
											<Badge variant={isQualified ? 'success' : 'secondary'}>{INTEGER_FORMATTER.format(finalTickets)} tickets</Badge>
											<Badge variant="outline">${NUMBER_FORMATTER.format(totalPrizePool)}</Badge>
										</div>
									</summary>
									<div className="grid gap-3 border-t border-border/40 p-4 sm:grid-cols-2">
										<div className="rounded-2xl border border-border/50 bg-background/70 p-3">
											<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Current stack</p>
											<div className="mt-3 flex items-start justify-between gap-3">
												<div>
													<p className="text-4xl font-semibold leading-none">{INTEGER_FORMATTER.format(finalTickets)}</p>
													<p className="mt-1 text-sm text-muted-foreground">
														{isQualified ? 'Projected tickets right now' : 'Projected once you qualify'}
													</p>
												</div>
												<Badge variant={hasActiveBonus ? 'warning' : 'outline'}>×{NUMBER_FORMATTER.format(finalMultiplier)}</Badge>
											</div>
											<div className="mt-3 rounded-2xl border border-border/50 bg-card/70 p-3">
												<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Formula</p>
												<p className="mt-1 text-sm text-muted-foreground">{ticketFormulaText}</p>
											</div>
											<div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
												<span className="rounded-full border border-border/50 bg-card/70 px-2.5 py-1 text-foreground">
													NFT boosts {ownedBonusCount}/{ticketing.bonusNfts.length}
												</span>
												<span className="rounded-full border border-border/50 bg-card/70 px-2.5 py-1 text-foreground">
													Bonus tickets +{INTEGER_FORMATTER.format(bonusTickets)}
												</span>
											</div>
										</div>

										<div className="rounded-2xl border border-border/50 bg-background/70 p-3">
											<div className="flex items-center justify-between gap-3">
												<div className="flex items-center gap-3">
													<div className="rounded-2xl bg-success/15 p-2 text-success">
														<Trophy className="h-5 w-5" />
													</div>
													<div>
														<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Prize pool</p>
														<h3 className="text-lg font-semibold">{totalWinnerCount} winners total</h3>
													</div>
												</div>
												<Badge variant="success">${NUMBER_FORMATTER.format(totalPrizePool)}</Badge>
											</div>
											<div className="mt-3 grid gap-2 text-sm">
												<div className="rounded-2xl border border-border/50 bg-card/70 px-3 py-2.5">
													<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Draw</p>
													<p className="mt-1">{drawPrizeSummary}</p>
												</div>
												<div className="rounded-2xl border border-border/50 bg-card/70 px-3 py-2.5">
													<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Longest streak</p>
													<p className="mt-1">${NUMBER_FORMATTER.format(prizes.streakLeader)} bonus for the top CoinFlip streak.</p>
												</div>
											</div>
										</div>
									</div>
								</details>
									<SaintPatrickStreakLeaderCard />
								</div>
							</div>

						<DialogFooter className="border-t border-border/40 bg-background/95 p-4">
							<Button variant={isLogged ? 'primary' : 'secondary'} className="w-full py-3 text-lg" onClick={handlePrimaryAction}>
								{isLogged ? 'Play CoinFlip now' : 'Connect wallet to start'}
							</Button>
						</DialogFooter>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default SaintPatrickWelcomeModal;
